import { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, Clock, AlertCircle } from 'lucide-react';
import { usePortfolioScopeParams } from '@/lib/portfolioScopeParams';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PaydayPulseCards — Round 84k (B).
 *
 * Surfaces actionable income events from the user's portfolio:
 *   • "Payday today / tomorrow" — pay dates in the next 48h
 *   • "Going ex-dividend in N days" — ex-div dates in the next 5d so
 *     the user knows the buy-cutoff for receiving the next payout.
 *
 * Pure projection over GET /api/portfolio/paycheck (pay events) and
 * GET /api/portfolio/dividend-calendar (ex events) — no new endpoints
 * needed. Self-hides when the user has no relevant events.
 */
function dayDiff(iso) {
  if (!iso) return null;
  try {
    const [y, m, d] = iso.split('-').map(Number);
    const target = Date.UTC(y, m - 1, d);
    const today = new Date();
    const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
    return Math.round((target - todayUtc) / (24 * 60 * 60 * 1000));
  } catch { return null; }
}

export default function PaydayPulseCards({ refreshKey = 0, onStockClick }) {
  const [paychecks, setPaychecks] = useState([]);
  const [exDivs, setExDivs] = useState([]);
  const scopeParams = usePortfolioScopeParams({ withWatchlistFlag: true });
  const scopeKey = `${scopeParams.portfolio_id || 'all'}|${scopeParams.include_watchlist === false ? 'po' : 'both'}`;

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/portfolio/paycheck`, { params: { days: 7, ...scopeParams }, withCredentials: true }).catch(() => null),
      axios.get(`${API}/portfolio/dividend-calendar`, { params: scopeParams, withCredentials: true }).catch(() => null),
    ]).then(([p, c]) => {
      if (cancelled) return;
      setPaychecks(p?.data?.payments || []);
      // Filter ex-div events in the next 5 days, from calendar events
      const evs = c?.data?.events || [];
      const today = new Date();
      const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
      const cutoff = todayUtc + 5 * 24 * 60 * 60 * 1000;
      const upcoming = evs
        .filter((e) => e.type === 'ex_dividend_date')
        .map((e) => {
          const dd = dayDiff(e.date);
          return { ...e, day_diff: dd };
        })
        .filter((e) => e.day_diff != null && e.day_diff >= 0 && e.day_diff <= 5);
      // Dedupe by ticker — show only the nearest occurrence per ticker
      const seen = new Set();
      const dedup = [];
      upcoming.sort((a, b) => a.day_diff - b.day_diff).forEach((e) => {
        if (!seen.has(e.ticker)) { seen.add(e.ticker); dedup.push(e); }
      });
      setExDivs(dedup.slice(0, 6));
    });
    return () => { cancelled = true; };
  }, [refreshKey, scopeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pay events: today (0) or tomorrow (1)
  const todayPays = paychecks.filter((p) => dayDiff(p.date) === 0);
  const tomorrowPays = paychecks.filter((p) => dayDiff(p.date) === 1);
  const exSoon = exDivs.filter((e) => e.day_diff <= 3);
  const exLater = exDivs.filter((e) => e.day_diff > 3);

  if (todayPays.length === 0 && tomorrowPays.length === 0 && exDivs.length === 0) {
    return null;
  }

  return (
    <div className="rounded-xl border border-violet-300/40 dark:border-violet-700/40 bg-gradient-to-br from-violet-50/40 to-fuchsia-50/30 dark:from-violet-950/20 dark:to-fuchsia-950/10 p-4 mb-4" data-testid="payday-pulse-cards">
      <div className="flex items-center gap-2 mb-3">
        {/* Round 92u — Sparkles icon pulses while the card is on
            screen. Because the card itself only mounts when there's
            an upcoming payday/ex-div event (see early-return above),
            the pulse animation acts as a subtle "something is
            happening" cue right when it matters most. `animate-pulse`
            uses opacity, so it works identically in light + dark
            themes without needing per-theme overrides. */}
        <Sparkles className="w-4 h-4 text-violet-600 dark:text-violet-400 animate-pulse" />
        <h3 className="text-sm font-bold">Income Pulse</h3>
        <span className="text-[11px] text-muted-foreground">· events from your holdings</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {todayPays.length > 0 && (
          <PulseBlock
            title="Paying today"
            tone="emerald"
            items={todayPays.map((p) => ({
              key: p.date + p.tickers.join(','),
              left: p.tickers.join(', '),
              right: `$${(p.amount || 0).toFixed(2)}`,
              hint: 'Cash hitting today',
              tickers: p.tickers,
              ineligibleShares: p.ineligible_shares || 0,
            }))}
            onStockClick={onStockClick}
            testid="pulse-paying-today"
          />
        )}
        {tomorrowPays.length > 0 && (
          <PulseBlock
            title="Paying tomorrow"
            tone="sky"
            items={tomorrowPays.map((p) => ({
              key: p.date + p.tickers.join(','),
              left: p.tickers.join(', '),
              right: `$${(p.amount || 0).toFixed(2)}`,
              hint: 'DRIP / cash decision tomorrow',
              tickers: p.tickers,
              ineligibleShares: p.ineligible_shares || 0,
            }))}
            onStockClick={onStockClick}
            testid="pulse-paying-tomorrow"
            // Round 92u — the DRIP-decision amount mirrors the
            // "Paid This Month" total: same emerald palette, 2px
            // larger (text-sm → text-base) so the dollar figure
            // reads as the headline of this card.
            amountClassName="font-mono font-bold text-base text-emerald-700 dark:text-emerald-300 shrink-0"
          />
        )}
        {exSoon.length > 0 && (
          <PulseBlock
            title="Going ex-div soon"
            tone="amber"
            icon={AlertCircle}
            items={exSoon.map((e) => ({
              key: e.date + e.ticker,
              left: e.ticker,
              right: e.day_diff === 0 ? 'today' : e.day_diff === 1 ? 'tomorrow' : `${e.day_diff} days`,
              hint: e.day_diff === 0 ? 'Already past — sale today missed payout' : 'Buy before this date to receive next payout',
              tickers: [e.ticker],
            }))}
            onStockClick={onStockClick}
            testid="pulse-ex-soon"
          />
        )}
        {exLater.length > 0 && (
          <PulseBlock
            title="Ex-div this week"
            tone="violet"
            icon={Clock}
            items={exLater.map((e) => ({
              key: e.date + e.ticker,
              left: e.ticker,
              right: `${e.day_diff} days`,
              hint: 'Heads up — share-count cutoff coming',
              tickers: [e.ticker],
            }))}
            onStockClick={onStockClick}
            testid="pulse-ex-later"
          />
        )}
      </div>
    </div>
  );
}

function PulseBlock({ title, tone, icon: Icon, items, onStockClick, testid, amountClassName }) {
  const toneClasses = {
    emerald: 'border-emerald-300/60 dark:border-emerald-700/60 bg-emerald-50/60 dark:bg-emerald-950/20',
    sky: 'border-sky-300/60 dark:border-sky-700/60 bg-sky-50/60 dark:bg-sky-950/20',
    amber: 'border-amber-300/60 dark:border-amber-700/60 bg-amber-50/60 dark:bg-amber-950/20',
    violet: 'border-violet-300/60 dark:border-violet-700/60 bg-violet-50/60 dark:bg-violet-950/20',
  }[tone];
  // Default amount tone — caller can override (e.g. tomorrow-DRIP
  // block bumps to emerald + text-base per Round 92u).
  const amountClass = amountClassName || 'font-mono font-semibold text-sm shrink-0';
  return (
    <div className={`rounded-lg border p-3 ${toneClasses}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-wider font-bold opacity-80">
        {Icon ? <Icon className="w-3 h-3" /> : null}
        {title}
      </div>
      <div className="space-y-1.5">
        {items.map((it) => (
          <div key={it.key} className="text-xs">
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => it.tickers.length === 1 && onStockClick?.(it.tickers[0])}
                className="font-bold text-sm hover:text-primary transition-colors text-left truncate"
              >
                {it.left}
              </button>
              <span className={amountClass}>{it.right}</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">{it.hint}</p>
            {/* Round 92o++++++++N+++++++++++ (Feb 2026) — ineligibility
                chip. Surfaces when the user holds lots bought AFTER the
                most recent ex-dividend date for any of these tickers —
                those lots aren't entitled to this cycle's payment, so
                the dollar amount above is intentionally lower than
                (total shares × per-share). Pre-empts the "why is this
                smaller than I expected?" question. */}
            {it.ineligibleShares > 0 && (
              <p
                className="text-[10px] text-amber-700 dark:text-amber-300 mt-1 inline-flex items-center gap-1"
                title="Lots purchased on or after this cycle's ex-dividend date aren't entitled to this payment. They'll start collecting on the next cycle."
                data-testid={`pulse-ineligible-chip`}
              >
                <AlertCircle className="w-3 h-3" />
                {Number.isInteger(it.ineligibleShares)
                  ? it.ineligibleShares
                  : it.ineligibleShares.toFixed(3).replace(/\.?0+$/, '')}
                {' share' + (it.ineligibleShares === 1 ? '' : 's')} not yet eligible
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
